import { useCallback, useEffect, useRef, useState } from "react";
import { exportCompaniesApi } from "../../api/searchApi";
import ExportReviewDialog from "../../components/ExportReviewDialog";
import {
  buildWorkspaceExportReview,
  collectWorkspaceCompanyIds,
  downloadCsvBlob,
  validWorkspaceCompanyIds,
} from "../../helper/csvExport";
import { errorDetail } from "../../helper/errorDetail";
import { isMockSavedSearchId } from "../../fixture/mockSavedSearches";

async function decodeExportErrorDetail(data) {
  if (!(typeof Blob !== "undefined" && data instanceof Blob)) return undefined;
  try {
    const text = typeof data.text === "function"
      ? await data.text()
      : await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.readAsText(data);
      });
    return JSON.parse(text)?.detail;
  } catch (_err) {
    return undefined;
  }
}

export function useSavedSearchExport({
  getWorkspaceDetail,
  historicalRows = [],
  id,
  isWorkspaceLoaded,
  isWorkspaceSnapshot,
  selectedIds,
  setError,
  setSelectedIds,
  workspace,
  workspaceId,
}) {
  const [exporting, setExporting] = useState(false);
  const [exportReview, setExportReview] = useState(null);
  const [exportError, setExportError] = useState(null);
  const reviewContext = useRef(null);
  reviewContext.current = { id, isWorkspaceSnapshot };
  const preparing = useRef(false);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [setSelectedIds, workspaceId]);

  useEffect(() => {
    if (!isWorkspaceSnapshot) setExportReview(null);
  }, [isWorkspaceSnapshot]);

  const prepareExport = useCallback(async (historicalSelection) => {
    if (!isWorkspaceLoaded || !isWorkspaceSnapshot || preparing.current) return;
    const selected = historicalSelection === null
      ? Array.from(selectedIds) : historicalSelection;
    const selectedCompanyIds = validWorkspaceCompanyIds(
      selected,
      historicalSelection === null ? workspace?.rows || [] : historicalRows
    );
    if (selected.length > 0 && selectedCompanyIds.length === 0) {
      setError(errorDetail(
        undefined,
        "The selected companies are unavailable for export. Select saved-search rows and try again."
      ));
      return;
    }
    const unavailableSelectedCount = selected.length - selectedCompanyIds.length;
    if (unavailableSelectedCount > 0) {
      setError(errorDetail(
        undefined,
        `${unavailableSelectedCount} selected ${unavailableSelectedCount === 1 ? "company is" : "companies are"} unavailable for export. Remove ${unavailableSelectedCount === 1 ? "it" : "them"} and try again.`
      ));
      return;
    }
    preparing.current = true;
    setExporting(true);
    setExportError(null);
    try {
      const companyIds = selected.length > 0 ? selectedCompanyIds
        : await collectWorkspaceCompanyIds(workspaceId, getWorkspaceDetail);
      if (reviewContext.current.id !== workspaceId || !reviewContext.current.isWorkspaceSnapshot) return;
      if (!companyIds?.length) {
        setError(errorDetail(undefined, companyIds
          ? "No active companies available for export."
          : "Failed to fetch saved-search companies for export. Try again."));
        return;
      }
      setExportReview({
        ...buildWorkspaceExportReview({
          name: workspace?.name,
          companyCount: companyIds.length,
          selectedCount: selected.length > 0 ? companyIds.length : null,
        }),
        selectedCompanyIds: companyIds,
        workspaceId,
      });
    } catch (_err) {
      if (reviewContext.current.id === workspaceId && reviewContext.current.isWorkspaceSnapshot) {
        setError(errorDetail(undefined, "Failed to fetch saved-search companies for export. Try again."));
      }
    } finally {
      preparing.current = false;
      setExporting(false);
    }
  }, [getWorkspaceDetail, historicalRows, isWorkspaceLoaded, isWorkspaceSnapshot, selectedIds, setError, workspace, workspaceId]);

  const onExport = useCallback(() => prepareExport(null), [prepareExport]);
  const onExportHistorical = useCallback((ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return;
    return prepareExport(ids);
  }, [prepareExport]);

  const onConfirmExport = useCallback(async (filename) => {
    if (!isWorkspaceSnapshot || !exportReview || exporting) return;
    const reviewWorkspaceId = exportReview.workspaceId;
    if (reviewWorkspaceId !== id) {
      setExportReview(null);
      setExportError(null);
      return;
    }
    if (isMockSavedSearchId(id)) {
      setExportError(errorDetail(undefined, "Export is unavailable for local preview saved searches."));
      return;
    }
    setExporting(true);
    setExportError(null);
    try {
      const companyIds = exportReview.selectedCompanyIds;
      if (!companyIds?.length) {
        setExportError(errorDetail(
          undefined,
          "Failed to fetch saved-search companies for export. Try again."
        ));
        return;
      }
      const response = await exportCompaniesApi({ company_ids: companyIds, workspace_id: reviewWorkspaceId });
      if (response?.status === 200) {
        downloadCsvBlob(response.data, filename);
        setExportReview(null);
      } else {
        setExportError(errorDetail(
          await decodeExportErrorDetail(response?.data),
          "Unable to export this saved search. Try again."
        ));
      }
    } catch (err) {
      setExportError(errorDetail(
        await decodeExportErrorDetail(err?.response?.data),
        "Unable to export this saved search. Try again."
      ));
    } finally {
      setExporting(false);
    }
  }, [exportReview, exporting, id, isWorkspaceSnapshot]);

  const onCancelExport = useCallback(() => {
    setExportReview(null);
    setExportError(null);
  }, []);

  return {
    exportDisabled: exporting || !isWorkspaceLoaded || !isWorkspaceSnapshot,
    exportError,
    exportReview,
    exporting,
    exportTitle: !isWorkspaceLoaded
      ? "Export is available after this saved search finishes loading."
      : (isWorkspaceSnapshot ? undefined : "Export is available only for saved-search results. Reset filters to export this saved search."),
    onCancelExport,
    onConfirmExport,
    onExport,
    onExportHistorical,
  };
}

export function SavedSearchExportDialog({ exportState }) {
  return <ExportReviewDialog
    open={Boolean(exportState.exportReview)}
    review={exportState.exportReview}
    busy={exportState.exporting}
    error={exportState.exportError}
    onCancel={exportState.onCancelExport}
    onConfirm={exportState.onConfirmExport}
  />;
}
